import first from 'lodash/first';
import last from 'lodash/last';
import { isGroupItem } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import { createSelector } from '@atlassian/react-sweet-state';
import { LEFT, RIGHT, UP, DOWN } from '../../common/constants.tsx';
import type { TableRef, Column } from '../../common/types/base-table.tsx';
import type { ListViewControllerData } from '../list-view-issues/types.tsx';
import type {
	State,
	SelectedCell,
	CopiedFromCell,
	CellCoordinates,
	CellCoordinatesById,
	Direction,
} from './types.tsx';

export const checkIsCellSelected = (
	selectedCell: SelectedCell,
	columnIndex: number,
	rowIndex: number,
) => (selectedCell ? selectedCell.column === columnIndex && selectedCell.row === rowIndex : false);

export const checkIsCellCopiedFrom = (
	copiedCell: CopiedFromCell,
	fieldId?: string,
	issueId?: string,
) => {
	if (!copiedCell || !fieldId || !issueId) {
		return false;
	}
	return copiedCell.fieldId === fieldId && copiedCell.issueId === issueId;
};

export const getCellSelectedSelector = createSelector(
	(state: State, coordinate: CellCoordinates & CellCoordinatesById) => ({
		isCellSelected: checkIsCellSelected(state.selectedCell, coordinate.column, coordinate.row),
		isCellCopiedFrom: checkIsCellCopiedFrom(
			state.copiedFromCell,
			coordinate.fieldId,
			coordinate.issueId,
		),
	}),
	({ isCellSelected, isCellCopiedFrom }) => ({
		isSelected: isCellSelected,
		isCopiedFrom: isCellCopiedFrom ?? false,
	}),
);

export const getHasSelectionSelector = createSelector(
	(state: State) => ({
		hasSelection: !!state.selectedCell,
	}),
	({ hasSelection }) => ({
		hasSelection,
	}),
);

const getNewColumnIndex = (
	initialCell: CellCoordinates,
	direction: Direction,
	totalColumns: number,
	totalRows: number,
) => {
	const { column: initialColumnIndex, row: initialRowIndex } = initialCell;

	const lastColumnIndex = totalColumns - 1;
	const lastRowIndex = totalRows - 1;

	const isFirstTableItem = initialColumnIndex === 0 && initialRowIndex === 0;
	const isLastTableItem =
		initialColumnIndex === lastColumnIndex && initialRowIndex === lastRowIndex;

	if (direction === LEFT && !isFirstTableItem) {
		return (initialColumnIndex || totalColumns) - 1;
	}

	if (direction === RIGHT && !isLastTableItem) {
		return (initialColumnIndex + 1) % totalColumns;
	}

	return initialColumnIndex;
};

const getNewRowIndex = (
	initialCell: CellCoordinates,
	direction: Direction,
	tableData: ListViewControllerData[],
) => {
	const { row: initialRowIndex } = initialCell;

	if (direction === UP) {
		let newRowIndex = initialRowIndex - 1;

		while (newRowIndex > 0 && isGroupItem(tableData[newRowIndex]?.issueId?.toString() || null)) {
			newRowIndex--;
		}

		return Math.max(0, newRowIndex);
	}

	if (direction === DOWN) {
		let nextRowIndex = initialRowIndex + 1;

		while (
			nextRowIndex < tableData.length &&
			isGroupItem(tableData[nextRowIndex]?.issueId?.toString() || null)
		) {
			nextRowIndex++;
		}

		return Math.min(tableData.length - 1, nextRowIndex);
	}

	return initialRowIndex;
};

export const getUpdatedNavigationIndexes = (
	initialCell: CellCoordinates,
	direction: Direction,
	tableColumn: Column<ListViewControllerData>[],
	tableData: ListViewControllerData[],
) => {
	const totalColumns = tableColumn.length;
	const totalRows = tableData.length;

	const newColumnIndex = getNewColumnIndex(initialCell, direction, totalColumns, totalRows);

	if (direction === UP || direction === DOWN) {
		const newRowIndex = getNewRowIndex(initialCell, direction, tableData);
		return { newColumnIndex, newRowIndex };
	}

	if (direction === RIGHT && newColumnIndex === 0) {
		const newRowIndex = getNewRowIndex(initialCell, DOWN, tableData);
		return { newColumnIndex, newRowIndex };
	}

	if (direction === LEFT && newColumnIndex === totalColumns - 1) {
		const newRowIndex = getNewRowIndex(initialCell, UP, tableData);
		return { newColumnIndex, newRowIndex };
	}

	return { newColumnIndex, newRowIndex: initialCell.row };
};

export const scrollCellIntoView = (
	tableRef: TableRef,
	visibleColumns: number[],
	direction: Direction,
	initialCell: CellCoordinates,
	newCell: CellCoordinates,
) => {
	const tableWidth = tableRef.current?.table?.props?.width || 0;
	const tableColumns = tableRef.current?.table?.props?.columns || [];

	const { column: initialColumnIndex, row: initialRowIndex } = initialCell;
	const { column: newColumnIndex, row: newRowIndex } = newCell;

	if (initialRowIndex !== newRowIndex) {
		setTimeout(() => tableRef.current?.scrollToRow(newRowIndex, 'auto'));
	}

	if (direction === LEFT) {
		const firstVisibleColumn = first(visibleColumns);
		const shouldScrollColumnIntoView =
			firstVisibleColumn === initialColumnIndex ||
			firstVisibleColumn === newColumnIndex ||
			newColumnIndex > initialColumnIndex;

		if (shouldScrollColumnIntoView) {
			const scrollAmount = tableColumns
				.slice(0, newColumnIndex)
				.reduce((acc, column) => acc + column.width, 0);

			setTimeout(() => tableRef.current?.scrollToLeft(scrollAmount));
		}
	}

	if (direction === RIGHT) {
		const lastVisibleColumn = last(visibleColumns);
		const shouldScrollColumnIntoView =
			lastVisibleColumn === initialColumnIndex ||
			lastVisibleColumn === newColumnIndex ||
			newColumnIndex < initialColumnIndex;

		if (shouldScrollColumnIntoView) {
			const totalWidthUntilNextCell = tableColumns
				.slice(0, newColumnIndex + 1)
				.reduce((acc, column) => acc + column.width, 0);

			const scrollAmount = Math.max(0, totalWidthUntilNextCell - tableWidth);
			setTimeout(() => tableRef.current?.scrollToLeft(scrollAmount));
		}
	}
};
